import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { ArrowLeft} from 'lucide-react';

const PrivacyPolicy = ({ isModal = false }) => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToTop();
  }, []);
  const isMobile = window.innerWidth < 768; 

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            {!isModal && !isMobile && (
              <Button
                onClick={() => window.history.back()}
                variant="outline"
                className="mb-6 group hover:bg-primary/10"
              >
                <ArrowLeft className="mr-2 h-4 w-4 group-hover:transform group-hover:-translate-x-1 transition-transform" />
                Back
              </Button>
            )}
            <h1 className="text-4xl md:text-5xl font-bold gradient-text-brand mb-4">
              Privacy Policy
            </h1>
            <p className="text-lg text-muted-foreground">
              <strong>Last Updated:</strong> November, 2025
            </p>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="prose prose-invert max-w-none"
          >
            <div className="bg-card/50 backdrop-blur-sm rounded-2xl p-8 border border-border/50 space-y-8">
              
              {/* Introduction */}
              <div className="space-y-4">
                <p className="text-foreground/90 leading-relaxed">
                  This Privacy Policy ("Policy") explains how Maha Naga Kali LLC, doing business as Vreels ("we", "us", or "our") collects, uses, discloses, and protects information about you when you access and use our website, mobile application, and related services (collectively, the "Services"). By using our Services, you agree to the collection and use of information in accordance with this Policy.
                </p>
              </div>

              {/* Section 1 */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-brand-orange">1. Information We Collect</h2>
                
                <div className="space-y-3">
                  <h3 className="text-xl font-semibold text-grad-orange-1">a. Information You Provide</h3>
                  <p className="text-foreground/90">
                    We may collect information that you directly provide to us, including but not limited to:
                  </p>
                  <ul className="space-y-2 text-foreground/90 list-disc list-inside ml-4">
                    <li><strong>Account Information:</strong> Such as your name, Phone number, email address, Google Account Signin, username, and password.</li>
                    <li><strong>Profile Data:</strong> Profile pictures, biographies, or other details you choose to share.</li>
                    <li><strong>User Content:</strong> Photos, videos, comments, and other content you upload or post.</li>
                    <li><strong>Communications:</strong> Information contained in messages, emails, or customer support interactions.</li>
                    <li><strong>Finding Friends:</strong> We use your uploaded contacts to help you find and connect with friends on the platform. This information is used solely for this purpose and is not shared with third parties.
</li>
                  </ul>
                </div>

                <div className="space-y-3">
                  <h3 className="text-xl font-semibold text-grad-orange-1">b. Information We Collect Automatically</h3>
                  <p className="text-foreground/90">
                    When you access or use our Services, we automatically collect certain information, including:
                  </p>
                  <ul className="space-y-2 text-foreground/90 list-disc list-inside ml-4">
                    <li><strong>Usage Data:</strong> Details of how you interact with our Services, such as the pages you visit, features you use, and the actions you take.</li>
                    <li><strong>Device Information:</strong> Information about the device you use to access our Services, such as device type, operating system, and unique device identifiers.</li>
                    <li><strong>Location Information:</strong> Data on your geographic location, if you permit location-based services. This information may be shown to your following friends.</li>
                    <li><strong>Cookies and Tracking Technologies:</strong> We use cookies, web beacons, and similar technologies to collect information about your browsing activities.</li>
                  </ul>
                </div>
              </div>

              {/* Section 2 */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-brand-orange">2. How We Use Your Information</h2>
                <p className="text-foreground/90">
                  We use the collected information for various purposes, including to:
                </p>
                <ul className="space-y-2 text-foreground/90 list-disc list-inside ml-4">
                  <li>Provide, maintain, and improve our Services.</li>
                  <li>Personalize your experience and deliver content tailored to your interests.</li>
                  <li>Communicate with you about updates, promotions, and news related to our Services.</li>
                  <li>Analyze usage patterns and trends to enhance our platform.</li>
                  <li>Enforce our Terms of Service and prevent fraud or other unauthorized activities.</li>
                  <li>Comply with legal obligations and protect our legal rights.</li>
                </ul>
              </div>

              {/* Section 3 */}
              {/* <div className="space-y-4">
                <h2 className="text-2xl font-bold text-brand-orange">3. How We Share Your Information</h2>
                <p className="text-foreground/90">
                  We may share your information in the following circumstances:
                </p>
                <ul className="space-y-2 text-foreground/90 list-disc list-inside ml-4">
                  <li><strong>With Service Providers:</strong> We share information with third-party service providers who perform services on our behalf, such as hosting, data analysis, payment processing, and customer support.</li>
                  <li><strong>With Business Partners:</strong> In connection with certain partnerships, we may share data to provide integrated services or promotions.</li>
                  <li><strong>Legal Compliance:</strong> We may disclose your information when required by law, regulation, or legal process, or to protect the rights, property, or safety of vreels, our users, or others.</li>
                  <li><strong>Business Transfers:</strong> If we are involved in a merger, acquisition, or asset sale, your information may be transferred as part of that transaction.</li>
                  <li><strong>With Your Consent:</strong> We may share your information with third parties when you have provided your explicit consent to do so.</li>
                </ul>
              </div> */}

              {/* Section 4 */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-brand-orange">3. Cookies and Tracking Technologies</h2>
                <p className="text-foreground/90">
                  We use cookies and similar tracking technologies to enhance your experience on our Services. Cookies help us understand user behavior, remember your preferences, and provide personalized content. You can manage your cookie preferences through your browser settings. However, please note that disabling cookies may affect the functionality of our Services.
                </p>
              </div>

              {/* Section 5 */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-brand-orange">4. Data Security and Retention</h2>
                <p className="text-foreground/90">
                  We implement reasonable security measures to protect your information from unauthorized access, alteration, disclosure, or destruction. Despite these measures, no security system is impenetrable, and we cannot guarantee the absolute security of your data.
                </p>
                <p className="text-foreground/90">
                  We retain your information for as long as necessary to fulfill the purposes outlined in this Policy unless a longer retention period is required or permitted by law.
                </p>
              </div>

              {/* Section 6 */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-brand-orange">5. Your Rights and Choices</h2>
                <p className="text-foreground/90">
                  Depending on your jurisdiction, you may have rights regarding your personal information, including the right to:
                </p>
                <ul className="space-y-2 text-foreground/90 list-disc list-inside ml-4">
                  <li>Access, correct, or delete your personal data.</li>
                  <li>Restrict or object to the processing of your personal data.</li>
                  <li>Withdraw your consent, where applicable.</li>
                  <li>Lodge a complaint with a data protection authority.</li>
                </ul>
                <p className="text-foreground/90">
                  To exercise any of these rights or for any questions about your personal data, please contact us using the information provided in the "Contact Us" section below.
                </p>
              </div>

              {/* Section 7 */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-brand-orange">6. Children's Privacy</h2>
                <p className="text-foreground/90">
                  Our Services are not directed to children under the age of 13. We do not knowingly collect personal information from children under 13. If you become aware that a child has provided us with personal information without parental consent, please contact us immediately, and we will take steps to remove the information promptly.
                </p>
              </div>

              {/* Section 8 - NSFW Content & Minor Protection Policy */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-brand-orange">7. NSFW Content & Minor Protection Policy</h2>

                <div className="space-y-3">
                  <h3 className="text-xl font-semibold text-grad-orange-1">a. Age Restriction</h3>
                  <p className="text-foreground/90">
                    Use of the Platform to post, or interact with Not Safe for Work ("NSFW") content is strictly limited to individuals 18 years of age or older (or the age of majority in their jurisdiction, if higher). By creating an account and accessing the Platform, users affirm that they meet the minimum age requirement for NSFW content.
                  </p>
                </div>

                <div className="space-y-3">
                  <h3 className="text-xl font-semibold text-grad-orange-1">b. Prohibition for Underage Users</h3>
                  <p className="text-foreground/90">
                    Accounts registered as under the age of 18 are strictly prohibited from accessing NSFW content.
                    The Platform will apply age-gating mechanisms and content filters to prevent exposure of NSFW material to underage users.
                    Any attempt to bypass age restrictions constitutes a violation of these Terms and may result in immediate account suspension or termination.
                  </p>
                </div>

                <div className="space-y-3">
                  <h3 className="text-xl font-semibold text-grad-orange-1">c. User Obligations</h3>
                  <p className="text-foreground/90">
                    Attempts to disguise NSFW material may result in removal of the content and sanctions on the account, including suspension or termination.
                    Users are solely responsible for the content they post and for ensuring it complies with all applicable laws and Platform rules.
                  </p>
                </div>

                <div className="space-y-3">
                  <h3 className="text-xl font-semibold text-grad-orange-1">d. Automated Moderation Disclaimer</h3>
                  <p className="text-foreground/90">
                    The Platform uses a combination of automated detection technologies, human moderation, and community reporting to identify NSFW material.
                    While the Platform endeavors to detect and restrict NSFW content accurately, no system is 100% effective. Users acknowledge and accept that some NSFW content may not be detected immediately.
                  </p>
                </div>

                <div className="space-y-3">
                  <h3 className="text-xl font-semibold text-grad-orange-1">e. Reporting Undetected Content</h3>
                  <p className="text-foreground/90">
                    Users are encouraged to report NSFW content that has been mislabeled, undetected, or improperly displayed to underage accounts.
                    Reported content will be reviewed by the Platform within a reasonable timeframe, typically within 24 hours.
                    The Platform reserves the right to remove or restrict any content at its sole discretion, with or without notice.
                  </p>
                </div>

                <div className="space-y-3">
                  <h3 className="text-xl font-semibold text-grad-orange-1">f. Zero-Tolerance for Illegal Content</h3>
                  <p className="text-foreground/90">
                    Any content that depicts or promotes child sexual abuse material (CSAM), exploitation of minors, non-consensual acts, or other illegal categories is strictly prohibited.
                    Such content will result in immediate permanent account termination and referral to appropriate law enforcement authorities.
                  </p>
                </div>

                <div className="space-y-3">
                  <h3 className="text-xl font-semibold text-grad-orange-1">g. Liability Limitation</h3>
                  <p className="text-foreground/90">
                    The Platform shall not be held liable for incidental or accidental exposure of underage users to NSFW content, provided that reasonable safeguards (including age verification, automated detection, and moderation practices) are in place.
                    Users acknowledge that responsibility for preventing underage access also lies with parents and guardians, who are encouraged to monitor usage of the Platform by minors.
                  </p>
                </div>

                <div className="space-y-3">
                  <h3 className="text-xl font-semibold text-grad-orange-1">h. Enforcement</h3>
                  <p className="text-foreground/90">
                    The Platform may suspend, restrict, or permanently terminate accounts found in violation of this Policy.
                    Repeated violations may result in the user being permanently banned from the Platform.
                    The Platform reserves the right to update or amend this Policy at any time to reflect evolving laws, technologies, and community standards.
                  </p>
                </div>
              </div>

              {/* Section 9 */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-brand-orange">8. Changes to This Privacy Policy</h2>
                <p className="text-foreground/90">
                  We may update this Privacy Policy from time to time. When we make changes, we will update the "Last Updated" date at the top of the Policy. We encourage you to review this Policy periodically to stay informed about how we are protecting your information. Your continued use of our Services after any changes constitutes your acceptance of the revised Policy.
                </p>
              </div>

              {/* Section 10 */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-brand-orange">9. Contact Us</h2>
                <p className="text-foreground/90">
                  If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us at:
                </p>
                <div className="bg-card/30 p-4 rounded-lg border border-border/30">
                  <p className="text-foreground/90">
                    Maha Naga Kali LLC, DBA Vreels<br />
                    204 W J Boaz Rd, Saginaw, TX 76179<br />
                    <a href="mailto:mnkllc@vreels.com" className="text-brand-orange hover:text-grad-orange-1 transition-colors">
                      support@vreels.com
                    </a>
                  </p>
                </div>
              </div>

              {/* Back to Top Button */}
              <div className="text-center pt-8">
                <Button
                  onClick={scrollToTop}
                  className="btn-gradient-orange"
                >
                  Back to Top
                </Button>
              </div>

            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy; 
